/**
 * /diagnostic/roadmap — F-131 Roadmap (the prescription).
 *
 * The second diagnostic view (Results · Roadmap · History). Where the roadmap takes the score,
 * the single next move, the roadmap the business owns (or Alfie's suggested order until it has
 * one), and the six pillar panels. Everything scored comes from the server's stored result; the page
 * shows when it was computed and lets an owner or admin recalculate now. A paid surface: the
 * gate meets a Free org, a member reads, an owner or admin edits (the editable list arrives in
 * the next slice).
 */
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { useOrganization } from '@/contexts';
import { useOrgRole } from '@/hooks/use-org-role';
import { formatDateTimeShort } from '@/lib/dates';
import { isRoadmapEnabled, loadFeatureFlags } from '@/lib/feature-flags';
import { trackCoaching } from '@/lib/tracking/coaching-analytics';
import { useAnswerQuestion, useEnsureFreshState, useRecalculateNow, useRoadmapAccess, useRoadmapItems } from '@/hooks/use-roadmap';
import type { RoadmapAnswer } from '@/api/roadmap';
import type { AnswerProps } from '@/components/roadmap/StepList';
import { useRoadmapScore } from '@/hooks/use-roadmap-score';
import { DiagnosticViewTabs } from '@/components/roadmap/DiagnosticViewTabs';
import { RoadmapAccessGate } from '@/components/roadmap/RoadmapAccessGate';
import { RoadmapReachCard } from '@/components/roadmap/RoadmapReachCard';
import { NextMoveCard } from '@/components/next-move/NextMoveCard';
import { RoadmapView } from '@/components/roadmap/RoadmapView';
import { useRoadmapMove } from '@/components/roadmap/roadmap-move';
import { SuggestedOrderList } from '@/components/roadmap/SuggestedOrderList';

export const Route = createFileRoute('/diagnostic/roadmap')({
  // Behind the roadmap flag: re-await the (cached) flag query so a cold cache never bounces
  // a flag-on user, then send a dark feature's visitors back to Results.
  beforeLoad: async ({ context }) => {
    await loadFeatureFlags(context.queryClient);
    if (!isRoadmapEnabled()) throw redirect({ to: '/diagnostic/results' });
  },
  component: GatedRoadmapPage,
});

function GatedRoadmapPage() {
  return (
    <RoadmapAccessGate>
      <RoadmapPage />
    </RoadmapAccessGate>
  );
}

export function RoadmapPage() {
  const { organization } = useOrganization();
  const orgId = organization?.id ?? null;
  const { data: access } = useRoadmapAccess(orgId);
  const canEdit = access?.canEdit ?? false;
  const { isAdminOrOwner, isLoading: roleLoading } = useOrgRole();

  const score = useRoadmapScore(orgId, { enabled: access?.canRead ?? false });
  const { refreshing } = useEnsureFreshState(orgId, {
    canEdit,
    result: score.resultQuery.data,
    isFetched: score.resultQuery.isFetched,
  });
  const recalc = useRecalculateNow(orgId);
  const answerMutation = useAnswerQuestion(orgId);
  // Today's move, from the Home card's own query (ruling R1): while the card rests, the point it
  // is about is not answerable here either.
  const move = useRoadmapMove();
  const busy = refreshing || recalc.isPending;
  // The question step: the answer handler, which point is saving, and the last refusal.
  const answer: AnswerProps = {
    onAnswer: (assetId: string, value: RoadmapAnswer) => answerMutation.mutate({ assetId, answer: value }),
    pendingAssetId: answerMutation.isPending ? answerMutation.variables?.assetId ?? null : null,
    error: answerMutation.isError
      ? { assetId: answerMutation.variables?.assetId ?? null, message: answerMutation.error instanceof Error ? answerMutation.error.message : 'Could not save your answer.' }
      : null,
    restingAssetId: move.resting ? move.assetId : null,
    // An answer on the open move's point IS the move's response: the move rests, the lock above
    // closes the question, and the row reads back what was tapped rather than the stored result.
    recorded:
      answerMutation.data?.moveAnswered === true && answerMutation.variables
        ? { assetId: answerMutation.variables.assetId, answer: answerMutation.variables.answer }
        : null,
  };
  const items = useRoadmapItems(orgId, access?.canRead ?? false);
  const hasOwnRoadmap = (items.data?.length ?? 0) > 0;

  return (
    <div className="container mx-auto max-w-5xl space-y-6 px-4 py-6">
      <DiagnosticViewTabs />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold leading-tight tracking-[-0.02em] text-foreground">Your roadmap</h1>
        {(score.data || score.notCalculated) && (
          <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 print:hidden">
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {busy && <RefreshCw className="h-3 w-3 animate-spin" aria-hidden />}
              {busy
                ? 'Recalculating…'
                : score.data
                ? `Updated ${formatDateTimeShort(score.data.computedAt)}`
                : 'Not calculated yet'}
            </p>
            {canEdit && (
              <button
                type="button"
                onClick={() => {
                  trackCoaching({ action: 'roadmap_recalculated' });
                  recalc.mutate();
                }}
                disabled={busy}
                className="rounded-md border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-60"
              >
                Recalculate now
              </button>
            )}
            {recalc.isError && (
              <p className="w-full text-right text-xs text-destructive">Could not recalculate. Try again.</p>
            )}
          </div>
        )}
      </div>

      {score.isLoading && (
        <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Building your roadmap…
        </div>
      )}

      {score.isError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4" />
          {score.error instanceof Error ? score.error.message : 'Could not load your roadmap.'}
        </div>
      )}

      {score.notCalculated && !busy && (
        <p className="rounded-lg border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
          Your roadmap hasn't been calculated yet.
          {canEdit ? ' Press Recalculate now to build it.' : ' An owner or admin can calculate it.'}
        </p>
      )}

      {score.data && (
        <div className="space-y-8">
          {/*
            Where the roadmap takes the score (Paper "Roadmap top B", 17 Sep 2026). The two score
            cards that sat here moved to the Results tab, the one place the score is read.
          */}
          <RoadmapReachCard
            score={score.data.score}
            results={score.data.results}
            points={score.resultQuery.data?.points ?? {}}
          />

          {/*
            The coaching card, the same one Home shows, under the score (Davit, 16 Sep 2026). It
            replaced the one-line "Your next move" strip that sat between the suggested order
            and the pillars. The card gates itself (flag, plan, role) and reads its own move.
          */}
          <NextMoveCard
            organizationId={orgId}
            isAdminOrOwner={isAdminOrOwner}
            roleLoading={roleLoading}
            hideRoadmapLink
            surface="roadmap"
          />

          {!hasOwnRoadmap && (
            <SuggestedOrderList
              suggested={score.data.suggested}
              moveTypes={score.data.suggestedMoveTypes}
              stepsByAsset={score.data.stepsByAsset}
              notAssessed={score.data.notAssessed}
              canEdit={canEdit}
              answer={answer}
            />
          )}

          {!canEdit && (
            <p className="rounded-lg border border-primary/25 bg-primary/[0.04] px-4 py-3 text-sm text-muted-foreground">
              Reordering, Alfie and answering the diagnostic questions are for owners and admins.
            </p>
          )}

          <RoadmapView
            roadmap={score.data}
            notAssessed={score.data.notAssessed}
            canEdit={canEdit}
            stepsByAsset={score.data.stepsByAsset}
            answer={answer}
            results={{
              state: score.data.results,
              canConnect: isAdminOrOwner,
              fetchedAt: score.data.fetchedAt,
              period: score.data.period,
              platforms: score.data.platforms,
            }}
          />
        </div>
      )}
    </div>
  );
}
